/** An athlete's smoothed ground velocity (yards a second), speed and facing, shared by both athlete views. */
export interface Heading {
  vx: number;
  vz: number;
  speed: number;
  yaw: number;
}

export interface HeadingRates {
  /** How fast the smoothed velocity follows the frame's movement (per second). */
  speedRate: number;
  maxSpeedYps: number;
  /** Faster than this, the athlete faces the way it moves. */
  turnSpeedYps: number;
  /** How fast the facing turns toward what it wants (per second). */
  turnRate: number;
}

/**
 * One frame: smooth the velocity from this frame's movement, then turn toward the way the athlete moves, or
 * toward `face` when it stands (or when `hold` keeps the facing, as in a block).
 * The facing follows the smoothed velocity, not the frame's own step: the run simulation moves in fixed
 * steps, so on some screen frames nobody moves at all, and turning toward the standing facing on those
 * frames left chasers stuck halfway between the two, running sideways.
 */
export function stepHeading(h: Heading, dx: number, dz: number, dt: number, face: number | null, hold: boolean, r: HeadingRates): void {
  if (dt <= 0) return;
  const k = 1 - Math.exp(-r.speedRate * dt);
  h.vx += (dx / dt - h.vx) * k;
  h.vz += (dz / dt - h.vz) * k;
  h.speed = Math.min(Math.hypot(h.vx, h.vz), r.maxSpeedYps);
  let want = h.yaw;
  if (!hold && h.speed > r.turnSpeedYps) want = Math.atan2(h.vx, h.vz);
  else if (face !== null) want = face;
  let d = want - h.yaw;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  h.yaw += d * (1 - Math.exp(-r.turnRate * dt));
}
